import { c as _c } from "react/compiler-runtime";
import { ErrorBoundary, Provider } from '@rollbar/react';
import { Toast } from '@systemeio/ui';
import { GrowthbookClientProvider } from 'growthbook/client';
import { NextPage } from 'next';
import { SSRConfig, appWithTranslation } from 'next-i18next';
import nextI18NextConfig from '../../next-i18next.config';
import NextApp from 'next/app';
import type { AppContext, AppInitialProps, AppProps } from 'next/app';
import { Router } from 'next/router';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';
import '../../styles/nprogress.css';
import { ReactElement, ReactNode, useEffect } from 'react';
import { rollbar } from 'rollbar/instance';
import { avertaFont, FontVariableEnum, interFont } from 'shared/constants/fonts';
import DashboardLayout from 'shared/layout/components/dashboard-layout';
import { fixFailedToExecuteRemoveChildOnNode } from '@systemeio/util';
import { SWRConfig } from 'swr';
import '../../styles/globals.css';
import { META_DATA_FROM_SERVER_KEY, MetaDataFromServerInterface } from 'shared/hooks/use-metadata-from-server';
export type NextPageWithLayout<P = {}, R = P> = NextPage<PageProps> & {
  getLayout: (page: ReactElement<any>, browserTabTitle: PageProps['browserTabTitle']) => ReactNode;
  getSecondaryLayout: (page: ReactElement<P>) => ReactElement<R>;
};
export interface PageProps {
  browserTabTitle: string;
}
type AppPropsWithLayout = AppProps<PageProps> & MetaDataFromServerInterface & {
  pageProps: SSRConfig;
} & {
  Component: NextPageWithLayout;
};
fixFailedToExecuteRemoveChildOnNode();
const getDefaultPageLayout = (page: ReactElement<any>, browserTabTitle: PageProps['browserTabTitle']) => <DashboardLayout browserTabTitle={browserTabTitle}>{page}</DashboardLayout>;
function App(t0) {
  const $ = _c(18);
  const {
    Component,
    pageProps,
    hostOnServer
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useEffect(_temp3, t1);
  const getLayout = Component.getLayout ?? getDefaultPageLayout;
  const getSecondaryLayout = Component.getSecondaryLayout ?? _temp4;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <style jsx={true} global={true}>{`
        :root {
          ${FontVariableEnum.Inter}: ${interFont.style.fontFamily};
          ${FontVariableEnum.Averta}: ${avertaFont.style.fontFamily};
        }
      `}</style>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== hostOnServer) {
    t3 = {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      onErrorRetry: _temp5,
      fallback: {
        [META_DATA_FROM_SERVER_KEY]: {
          hostOnServer
        }
      }
    };
    $[2] = hostOnServer;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== Component || $[5] !== pageProps) {
    t4 = <Component {...pageProps} />;
    $[4] = Component;
    $[5] = pageProps;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== getLayout || $[8] !== getSecondaryLayout || $[9] !== pageProps.browserTabTitle || $[10] !== t4) {
    t5 = getLayout(getSecondaryLayout(t4), pageProps.browserTabTitle);
    $[7] = getLayout;
    $[8] = getSecondaryLayout;
    $[9] = pageProps.browserTabTitle;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Toast />;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5) {
    t7 = <GrowthbookClientProvider>{t5}{t6}</GrowthbookClientProvider>;
    $[13] = t5;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t3 || $[16] !== t7) {
    t8 = <>{t2}<Provider instance={rollbar}><ErrorBoundary><SWRConfig value={t3}>{t7}</SWRConfig></ErrorBoundary></Provider></>;
    $[15] = t3;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}
function _temp5(error, key, config, revalidate, t0) {
  const {
    retryCount
  } = t0;
  if (retryCount > 3) {
    if (error.status === 502 || error.status === 504) {
      return;
    }
  }
}
function _temp4(page) {
  return page;
}
function _temp3() {
  const handleRouteStart = _temp;
  const handleRouteDone = _temp2;
  Router.events.on("routeChangeStart", handleRouteStart);
  Router.events.on("routeChangeComplete", handleRouteDone);
  Router.events.on("routeChangeError", handleRouteDone);
  return () => {
    Router.events.off("routeChangeStart", handleRouteStart);
    Router.events.off("routeChangeComplete", handleRouteDone);
    Router.events.off("routeChangeError", handleRouteDone);
  };
}
function _temp2() {
  return NProgress.done();
}
function _temp() {
  return NProgress.start();
}
App.getInitialProps = async (context: AppContext): Promise<MetaDataFromServerInterface & AppInitialProps> => {
  const ctx = await NextApp.getInitialProps(context);
  const {
    req,
    res
  } = context.ctx;
  const hostOnServer = req?.headers?.host || null;
  if (req && res && req.url) {
    if (res.statusCode === 404) {
      const accept = req.headers['accept'] || '';
      const isHtmlRequest = accept.includes('text/html');

      // Only redirect actual page requests, not Next.js data fetching
      if (isHtmlRequest) {
        context.ctx.res?.writeHead(302, {
          Location: '/dashboard/not-found'
        });
        context.ctx.res?.end();
      }
    }
  }
  return {
    ...ctx,
    hostOnServer
  };
};
export default appWithTranslation(App, nextI18NextConfig);